import React from 'react';
import { SwiperSlide } from 'swiper/react';
import { Carousel, CarouselWrapper, CarouselArrowNavigation } from '$partials/base/carousel';
import ArticleListingItem from '$partials/items/article-listing-item';
import { Button } from '$partials/ui';
import IconChevron from '$partials/icons/icon-chevron';
import { SimpleImage } from '$partials/media';
import { formatPublicationDate } from '$utilities/data';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ArticleCarousel}
 */

const ArticleCarousel = ({
    featuredArticle,
    articleListing,
    items,
    heading
}) => {

    const swiperConfig = {
        slidesPerView: 'auto',
        spaceBetween: 0
    };

    return (
        <div className='module-article-carousel theme-light bg-main-background'>
            <div className='py-16 sm:py-40 md:py-48'>
                <Carousel swiperConfig={swiperConfig}>
                    <div className='flex flex-col w-full mx-auto gap-16 sm:gap-y-48 md:gap-24'>
                        {/* Header with title and controls */}
                        <div className='layout-container layout-site-outer-padding w-full theme-light'>
                            <div className='flex justify-between'>
                                {/* Title */}
                                {heading && articleListing?.url && featuredArticle && (
                                    <>
                                        <h2 className='type-style-2 text-main-text-primary w-full'>
                                            {heading}
                                        </h2>
                                        <Button
                                            as='a'
                                            className={`${featuredArticle ? 'block' : 'hidden'} type-style-eyebrow button-primary-transparent button-primary-transparent--small rounded flex gap-2 items-center justify-center`}
                                            href={articleListing?.url}>
                                            <p className='whitespace-nowrap'>
                                                view all
                                            </p>
                                            <IconChevron className='ml-10 -rotate-90' />
                                        </Button>
                                    </>
                                )}
                            </div>

                            {/* Featured article */}
                            {featuredArticle && (
                                <div
                                    className='flex flex-col md:flex-row col-wrapper mt-24 group'>
                                    {/* Property Image */}
                                    {featuredArticle.listingImage && (
                                        <figure className='relative w-full md:w-8/12 col-padding'>
                                            <SimpleImage
                                                {...featuredArticle.listingImage}
                                                className='w-full h-full object-cover transition-transform duration-300'/>
                                            <div className='absolute inset-0 opacity-0 group-hover:opacity-100 before:absolute before:inset-0 before:bg-[rgba(0,0,0,0.4)] before:mix-blend-saturation before:content-[""] after:absolute after:inset-0 after:bg-[rgba(30,37,165,0.5)] after:mix-blend-screen after:content-[""]' />
                                        </figure>
                                    )}


                                    {/* title and Tags */}
                                    <div className='flex flex-col col-padding md:w-4/12 md:gap-y-24 mt-16 md:mt-0'>
                                        {/* Details Wrap - Eyebrow and Date */}
                                        {(featuredArticle?.parentListingEyebrow || featuredArticle?.publicationDate) && (
                                            <div className='flex gap-8 items-center type-style-eyebrow w-full'>
                                                {featuredArticle?.parentListingEyebrow && (
                                                    <p className='text-main-brand-text'>
                                                        {featuredArticle.parentListingEyebrow}
                                                    </p>
                                                )}
                                                {featuredArticle?.parentListingEyebrow && featuredArticle?.publicationDate && (
                                                    <p className='text-main-text-primary'>
                                                        /
                                                    </p>
                                                )}
                                                {featuredArticle?.publicationDate && (
                                                    <p className='text-main-text-primary'>
                                                        {formatPublicationDate(featuredArticle.publicationDate)}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {featuredArticle.title && (
                                            <h3 className='type-style-2 text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text'>
                                                <a href={featuredArticle.url}>
                                                    {featuredArticle.title}
                                                </a>
                                            </h3>
                                        )}

                                        {featuredArticle.listingText && (
                                            <p
                                                className='type-style-body-r text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text'>
                                                {featuredArticle.listingText}
                                            </p>
                                        )}

                                        {featuredArticle?.topics?.length > 0 && (
                                            <div className='flex flex-wrap gap-6 w-full'>
                                                {featuredArticle.topics.map((topic, index) => (
                                                    <Button
                                                        as='a'
                                                        className='button-tag'
                                                        href={`/topics/${topic.slug}`}
                                                        key={index}>
                                                        <p className='type-style-eyebrow text-tag-main-text'>
                                                            {topic.title}
                                                        </p>
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Controls */}
                            <div className='items-center justify-end gap-16 flex theme-light'>
                                {/* Title */}
                                {heading && articleListing?.url && !featuredArticle && (
                                    <>
                                        <h2 className='type-style-2 text-main-text-primary w-full'>
                                            {heading}
                                        </h2>
                                        <div className='hidden sm:block'>
                                            <Button
                                                as='a'
                                                className='flex type-style-eyebrow button-primary-transparent button-primary-transparent--small gap-2 items-center justify-center'
                                                href={articleListing?.url}>
                                                <span className='whitespace-nowrap'>
                                                    view all
                                                </span>
                                                <IconChevron className='ml-10 -rotate-90' />
                                            </Button>
                                        </div>
                                    </>
                                )}
                                <div className='hidden sm:block'>
                                    <CarouselArrowNavigation />
                                </div>
                            </div>
                        </div>

                        {/* Carousel */}
                        {items?.length > 0 && (
                            <div className='overflow-hidden w-full max-w-screen left-1/2 relative -translate-x-1/2'>
                                <div className='layout-container layout-site-outer-padding overflow-visible'>
                                    <div className='col-wrapper'>
                                        <CarouselWrapper>
                                            {items.map((article, index) => (
                                                <SwiperSlide
                                                    className='w-10/12 sm:w-5/12 md:w-4/12 flex'
                                                    key={article.id || index}>
                                                    <ArticleListingItem
                                                        className='col-padding'
                                                        isCarouselItem={true}
                                                        {...article} />
                                                    <div className={`shrink-0 w-[1px] h-full bg-main-border ${index === items.length - 1 ? 'hidden' : ''}`} />
                                                </SwiperSlide>
                                            ))}
                                        </CarouselWrapper>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Carousel>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ArticleCarousel;
